import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { boronClient } from "../../api";
import { StudentTagFilterInterface } from "../../domains/StudentTag";
import { createError } from "../../errors";
import { buildStudentFilterParams } from "../../helpers/FiltersHelper";
import { getNextPageParam } from "../../helpers/ReactQueryHelper";
import ApiResponse from "../../interfaces/ApiResponse";
import KarteInterface, {
  KarteCommentInterface,
} from "../../interfaces/KarteInterface";

const path = "/api/v1/sections/{section_id}/kartes" as const;

const buildQueryKey = (
  sectionId: string,
  query?: StudentTagFilterInterface,
) => {
  return query ? [path, sectionId, query] : [path, sectionId];
};

export const useFetchSectionKartes = ({
  sectionId,
  query,
}: {
  sectionId: string;
  query: StudentTagFilterInterface;
}) => {
  const result = useInfiniteQuery<ApiResponse<KarteInterface[]>>({
    queryKey: buildQueryKey(sectionId, query),
    queryFn: async ({ pageParam }) => {
      const { response, data } = await boronClient.GET(path, {
        params: {
          path: { section_id: sectionId },
          query: {
            ...buildStudentFilterParams(query),
            page: pageParam,
          },
        },
      });
      if (response.ok && data) {
        return data.kartes;
      }
      throw await createError(response);
    },
    getNextPageParam,
    initialPageParam: 1,
  });

  const kartes = result.data?.pages.flatMap((page) => page.data || []);

  const loadMore = () => {
    if (result.hasNextPage && !result.isFetchingNextPage) {
      result.fetchNextPage();
    }
  };

  return {
    ...result,
    kartes,
    loadMore,
  };
};

export const useUpdateSectionKartesCache = () => {
  const queryClient = useQueryClient();

  // 指定IDのKarteのキャッシュを更新
  const updateKarteCache = (
    sectionId: string,
    updatedKarte: KarteInterface,
  ) => {
    const queryKey = buildQueryKey(sectionId);
    queryClient.setQueriesData<InfiniteData<ApiResponse<KarteInterface[]>>>(
      { queryKey },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.map((karte) =>
              karte.id === updatedKarte.id ? updatedKarte : karte,
            ),
          })),
        };
      },
    );
  };

  // 指定IDのKarteをキャッシュから削除
  const removeKarteFromCache = (sectionId: string, karteId: string) => {
    const queryKey = buildQueryKey(sectionId);

    queryClient.setQueriesData<InfiniteData<ApiResponse<KarteInterface[]>>>(
      { queryKey },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.filter((karte) => karte.id !== karteId),
          })),
        };
      },
    );
  };

  // 指定IDのKarteにコメントを追加
  const addKarteCommentCache = (
    sectionId: string,
    karteId: string,
    karteComment: KarteCommentInterface,
  ) => {
    const queryKey = buildQueryKey(sectionId);

    queryClient.setQueriesData<InfiniteData<ApiResponse<KarteInterface[]>>>(
      { queryKey },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.map((karte) => {
              // カルテIDが一致する場合はコメントを末尾に追加
              if (karte.id === karteId) {
                return {
                  ...karte,
                  comments: [...karte.comments, karteComment],
                };
              }
              return karte;
            }),
          })),
        };
      },
    );
  };

  // 指定IDのKarteのコメントを削除
  const removeKarteCommentCache = (
    sectionId: string,
    karteId: string,
    karteCommentId: string,
  ) => {
    const queryKey = buildQueryKey(sectionId);

    queryClient.setQueriesData<InfiniteData<ApiResponse<KarteInterface[]>>>(
      { queryKey },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.map((karte) => {
              // カルテIDが一致する場合はコメントを削除
              if (karte.id === karteId) {
                return {
                  ...karte,
                  comments: karte.comments.filter(
                    (comment) => comment.id !== karteCommentId,
                  ),
                };
              }
              return karte;
            }),
          })),
        };
      },
    );
  };

  return {
    updateKarteCache,
    removeKarteFromCache,

    addKarteCommentCache,
    removeKarteCommentCache,
  };
};
